import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { RootState } from '../redux/root';
import { viewInvoice } from '../Components/InvoiceTool/InvoiceUtil';
import { Loader } from '../atoms';
import { setHasViewClicked } from '../redux/slices/feesSlice';

const FeeSendFormViewInvoice = () => {
  const { invoicePdf, invoiceNumber } = useSelector((s: RootState) => s.fees);
  const [loader, setLoader] = useState<boolean>(false);
  const dispatch = useDispatch();

  const handleClick = useDebounce(() => {
    setLoader(false);
    viewInvoice(invoicePdf, invoiceNumber, true, dispatch);
  }, 3000);

  const isReady = invoicePdf !== '';

  return (
    <Row>
      <ViewInvoice
        type="button"
        $invoiceIsReady={isReady}
        disabled={!isReady}
        onClick={() => {
          if (!isReady) return;
          setLoader(true);
          handleClick();
        }}
      >
        <EyeIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
          <circle cx="12" cy="12" r="3" />
        </EyeIcon>
        View Invoice
        {loader && (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        )}
      </ViewInvoice>
    </Row>
  );
};

export default FeeSendFormViewInvoice;

const Row = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 0 16px;
`;

const ViewInvoice = styled.button<{ $invoiceIsReady: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  background: ${({ $invoiceIsReady }) => ($invoiceIsReady ? 'var(--primary-50)' : 'var(--neutral-50)')};
  color: ${({ $invoiceIsReady }) => ($invoiceIsReady ? 'var(--primary-700)' : 'var(--neutral-400)')};
  border: 1px solid ${({ $invoiceIsReady }) => ($invoiceIsReady ? 'var(--primary-200, var(--primary-100))' : 'var(--border)')};
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: ${({ $invoiceIsReady }) => ($invoiceIsReady ? 'pointer' : 'not-allowed')};
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  position: relative;

  &:hover:not(:disabled) {
    background: var(--primary-100);
    border-color: var(--primary-300, var(--primary-200));
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const EyeIcon = styled.svg`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const LoaderWrapper = styled.div`
  zoom: 0.5;
  position: absolute;
  right: -28px;
  top: 50%;
  transform: translateY(-50%);
`;

const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay],
  );

  return debouncedCallback;
};
